import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';

/**
 * Admin-driven parent onboarding: create a parent account and link it to an
 * athlete (their child). Powers the parent phase — until a parent is linked,
 * nothing parent-facing (messages, calendar, meets) reaches anyone.
 */
function AdminParents() {
  const { user } = useAuth();
  const toast = useToast();
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };

  const [parents, setParents] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const emptyCreate = { name: '', email: '', password: '', phone: '', athlete_ids: [], relationship: 'parent', is_primary: true };
  const [createForm, setCreateForm] = useState(emptyCreate);
  const emptyLink = { parent_id: '', athlete_ids: [], relationship: 'parent', is_primary: false };
  const [linkForm, setLinkForm] = useState(emptyLink);
  const [saving, setSaving] = useState(false);

  const toggleId = (arr, id) => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, l] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/users?role=parent&active_only=true`, auth),
        axios.get(`${API_BASE_URL}/athletes`, auth),
        axios.get(`${API_BASE_URL}/api/admin/parent-links`, auth)
      ]);
      setParents(Array.isArray(p.data) ? p.data : []);
      setAthletes(Array.isArray(a.data) ? a.data : []);
      setLinks(Array.isArray(l.data) ? l.data : []);
    } catch (err) {
      toast.error('Failed to load parents');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const linkParentToAthlete = (parentId, athleteId, relationship, isPrimary) =>
    axios.post(`${API_BASE_URL}/api/admin/parent-athlete-link`,
      { parentId, athleteId, relationship, isPrimary }, auth);

  const handleCreateParent = async (e) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Name, email and password are required');
      return;
    }
    if (createForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/users`, {
        name: createForm.name, email: createForm.email, password: createForm.password,
        role: 'parent', phone: createForm.phone
      }, auth);
      const parentId = res.data?.user?.id;
      if (parentId && createForm.athlete_ids.length) {
        for (const aid of createForm.athlete_ids) {
          await linkParentToAthlete(parentId, aid, createForm.relationship, createForm.is_primary);
        }
      }
      const n = createForm.athlete_ids.length;
      toast.success(n ? `Parent created & linked to ${n} child${n === 1 ? '' : 'ren'}` : 'Parent created');
      setCreateForm(emptyCreate);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create parent');
    } finally {
      setSaving(false);
    }
  };

  const handleLink = async (e) => {
    e.preventDefault();
    if (!linkForm.parent_id || linkForm.athlete_ids.length === 0) {
      toast.error('Pick a parent and at least one athlete');
      return;
    }
    setSaving(true);
    try {
      for (const aid of linkForm.athlete_ids) {
        await linkParentToAthlete(linkForm.parent_id, aid, linkForm.relationship, linkForm.is_primary);
      }
      toast.success(`Linked ${linkForm.athlete_ids.length} child(ren)`);
      setLinkForm(emptyLink);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to link');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (id) => {
    if (!window.confirm('Remove this parent–athlete link?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/parent-athlete-link/${id}`, auth);
      load();
    } catch (err) {
      toast.error('Failed to unlink');
    }
  };

  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 16 };
  const label = { display: 'block', fontSize: 13, fontWeight: 600, margin: '8px 0 2px' };
  const input = { width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 20 }}>
      <h1>👪 Parents</h1>
      <p style={{ color: '#666' }}>Create a parent account and link it to their child so they can see schedule, announcements, messages and meets.</p>

      {/* Create parent (+ optional immediate link) */}
      <div style={card}>
        <h2 style={{ marginTop: 0 }}>Add a parent</h2>
        <form onSubmit={handleCreateParent}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={label}>Name *</label>
              <input style={input} value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })} />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={label}>Email *</label>
              <input style={input} type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={label}>Temporary password * (8+ chars)</label>
              <input style={input} value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })} />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={label}>Phone</label>
              <input style={input} value={createForm.phone} onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 240px' }}>
              <label style={label}>Link to children (optional — pick as many as apply)</label>
              <div style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: 8, maxHeight: 140, overflowY: 'auto' }}>
                {athletes.length === 0 && <small>No athletes.</small>}
                {athletes.map(a => (
                  <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 2px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={createForm.athlete_ids.includes(a.id)}
                      onChange={() => setCreateForm({ ...createForm, athlete_ids: toggleId(createForm.athlete_ids, a.id) })}
                      style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }} />
                    {a.name}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label style={label}>Relationship</label>
              <select style={input} value={createForm.relationship} onChange={e => setCreateForm({ ...createForm, relationship: e.target.value })}>
                <option value="parent">Parent</option>
                <option value="guardian">Guardian</option>
                <option value="emergency_contact">Emergency contact</option>
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <input type="checkbox" checked={createForm.is_primary} onChange={e => setCreateForm({ ...createForm, is_primary: e.target.checked })} />
                Primary contact
              </label>
            </div>
          </div>
          <button className="btn-primary" type="submit" disabled={saving} style={{ marginTop: 12 }}>
            {saving ? 'Saving…' : 'Create parent'}
          </button>
        </form>
      </div>

      {/* Link an existing parent */}
      <div style={card}>
        <h2 style={{ marginTop: 0 }}>Link an existing parent to children</h2>
        <p style={{ color: '#666', marginTop: 0 }}>A parent can be linked to several kids; a child can have several parents (e.g. mom + dad).</p>
        <form onSubmit={handleLink} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={label}>Parent</label>
            <select style={input} value={linkForm.parent_id} onChange={e => setLinkForm({ ...linkForm, parent_id: e.target.value })}>
              <option value="">Select parent…</option>
              {parents.map(p => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
            </select>
            <label style={{ ...label, marginTop: 8 }}>Relationship</label>
            <select style={input} value={linkForm.relationship} onChange={e => setLinkForm({ ...linkForm, relationship: e.target.value })}>
              <option value="parent">Parent</option>
              <option value="guardian">Guardian</option>
              <option value="emergency_contact">Emergency contact</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              <input type="checkbox" checked={linkForm.is_primary} onChange={e => setLinkForm({ ...linkForm, is_primary: e.target.checked })} />
              Primary contact
            </label>
          </div>
          <div style={{ flex: '1 1 240px' }}>
            <label style={label}>Children (pick as many as apply)</label>
            <div style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: 8, maxHeight: 160, overflowY: 'auto' }}>
              {athletes.length === 0 && <small>No athletes.</small>}
              {athletes.map(a => (
                <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 2px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={linkForm.athlete_ids.includes(a.id)}
                    onChange={() => setLinkForm({ ...linkForm, athlete_ids: toggleId(linkForm.athlete_ids, a.id) })}
                    style={{ width: 16, height: 16, flexShrink: 0, margin: 0 }} />
                  {a.name}
                </label>
              ))}
            </div>
            <button className="btn-primary" type="submit" disabled={saving} style={{ marginTop: 8 }}>Link selected</button>
          </div>
        </form>
      </div>

      {/* Existing links */}
      <div style={card}>
        <h2 style={{ marginTop: 0 }}>Linked families ({links.length})</h2>
        {loading ? <p>Loading…</p> : links.length === 0 ? (
          <p style={{ color: '#888' }}>No parents linked yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #eee' }}>Parent</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #eee' }}>Child</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #eee' }}>Relationship</th>
                <th style={{ padding: 8, borderBottom: '2px solid #eee' }}></th>
              </tr>
            </thead>
            <tbody>
              {links.map(l => (
                <tr key={l.id}>
                  <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                    <strong>{l.parent_name}</strong><br /><small style={{ color: '#888' }}>{l.parent_email}</small>
                  </td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>{l.athlete_name}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                    {l.relationship}{l.is_primary ? ' · primary' : ''}
                  </td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                    <button className="btn-cancel" onClick={() => handleUnlink(l.id)}>Unlink</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminParents;
